import React, { useEffect, useState, useRef, forwardRef, useMemo } from "react";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import api from "../Service/api";
import "./CustomerLedger.css";



const formatDateOnly = (dateTime) => {
    if (!dateTime) return "--";
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return "--";
    return date.toLocaleDateString();
  };

  // ✅ FIX: Get the correct transaction date based on invoice type
const getTransactionDateValue = (invoice) => {
  if (!invoice) return null;

  // Payment → use paymentTime
  if (invoice.isPayment) {
    return invoice.paymentTime || null;
  }

  // Return → prioritize returnRecordTime
  if (invoice.status === 'Return') {
    return invoice.returnRecordTime 
        || invoice.saleRecordTime 
        || invoice.purchaseRecordTime 
        || null;
  }

  // Sale / FactoryUse → use saleRecordTime, fallback to purchaseRecordTime
  return invoice.saleRecordTime 
      || invoice.purchaseRecordTime 
      || null;
};

  const formatCurrencyComma = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "0.00";
    }
    return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // ---- Helper functions for product size and detail ----
const getSizeDisplay = (product) => {
  const category = product.category?.toLowerCase() || '';
  if (category === 'roll') return product.size || '---';
  if (category === 'reel' || category === 'tota') return product.length || '---';
  if (category === 'packet') return product.size || '---';
  return product.size || '---';
};

const getDetail = (product) => {
  const category = product.category?.toLowerCase() || '';
  if (category === 'roll') return `Size: ${product.size}`;
  if (category === 'reel' || category === 'tota') return `Length: ${product.length}`;
  if (category === 'packet') {
    const parts = [];
    if (product.size) parts.push(`Size: ${product.size}`);
    if (product.ktae) parts.push(`KTAE: ${product.ktae}`);
    if (product.gram) parts.push(`Gram: ${product.gram}`);
    return parts.join(' | ');
  }
  return product.size ? `Size: ${product.size}` : '';
};
// ----------------------
// Ledger Print Component (unchanged)
// ----------------------
const LedgerPrint = forwardRef(({ ledgerData, customerName, startMonth, endMonth }, ref) => {
  const hasMonthLedgers = ledgerData.monthLedgers && ledgerData.monthLedgers.length > 0;


  const getTransactionImpact = (invoice) => {
  if (invoice.isPayment) {
    return -Number(invoice.amountPaid || 0);
  }
  const beforeGst = Number(invoice.totalBeforeGst || 0);
  const gst = Number(invoice.gstAmount || 0);
  const invoiceTotal = beforeGst + gst;
  // No subtraction of amountPaid – payment is a separate row
  return (invoice.status === 'Sale' || invoice.status === 'FactoryUse') ? invoiceTotal : -invoiceTotal;
};



  
  return (
    <div ref={ref} style={{ padding: "20px", fontFamily: "Arial, sans-serif", display: "none" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px", borderBottom: "2px solid #333", paddingBottom: "10px" }}>
        <h1 style={{ margin: 0, fontSize: "20px" }}>CUSTOMER LEDGER</h1>
        <h3 style={{ margin: "5px 0", fontSize: "16px", color: "#666" }}>{customerName || "All Customers"}</h3>
        <div style={{ fontSize: "12px", color: "#888" }}>
          Period: {startMonth || "Start"} to {endMonth || "End"}
        </div>
        <div style={{ fontSize: "10px", color: "#999", marginTop: "5px" }}>
          Generated on: {new Date().toLocaleString()}
        </div>
      </div>

      {hasMonthLedgers ? (
        ledgerData.monthLedgers.map((monthData, monthIdx) => {
          const transactions = monthData.invoices || [];
          let runningBalance = Number(monthData.monthStartBalance || 0);

          return (
            <div key={monthIdx} style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
              {/* Month summary */}
              <div
                style={{
                  background: "#333",
                  color: "white",
                  padding: "6px 10px",
                  fontWeight: "bold",
                  fontSize: "11px",
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span>Month: {monthData.billingMonth}</span>
                <span>Start Balance: {formatCurrencyComma(monthData.monthStartBalance)}</span>
                <span>End Balance: {formatCurrencyComma(monthData.monthEndBalance)}</span>
                <span>Sales: {formatCurrencyComma(monthData.monthTotalSales)}</span>
                <span>Returns: {formatCurrencyComma(monthData.monthTotalReturns)}</span>
                <span>Payments: {formatCurrencyComma(monthData.monthTotalPayments)}</span>
              </div>

              {/* Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
                <thead>
                  <tr style={{ background: "#e9ecef", borderBottom: "2px solid #000" }}>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "left", width: "10%" }}>Date</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "left", width: "8%" }}>Voucher #</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "left", width: "8%" }}>Size</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "left", width: "32%" }}>Description</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", width: "6%" }}>Qty</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", width: "8%" }}>Rate</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", width: "10%" }}>Debit</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", width: "10%" }}>Credit</th>
                    <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", width: "12%" }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Opening Balance */}
                  <tr style={{ background: "#f8f9fa", fontStyle: "italic" }}>
                    <td>{monthData.billingMonth}-01</td>
                    <td>---</td>
                    <td>---</td>
                    <td>Opening Balance</td>
                    <td style={{ textAlign: "right" }}>---</td>
                    <td style={{ textAlign: "right" }}>---</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrencyComma(monthData.monthStartBalance > 0 ? monthData.monthStartBalance : 0)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrencyComma(monthData.monthStartBalance < 0 ? Math.abs(monthData.monthStartBalance) : 0)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold" }}>
                      {formatCurrencyComma(monthData.monthStartBalance)}
                    </td>
                  </tr>

                  {/* Transactions */}
                  {transactions.map((invoice, idx) => {
                    const impact = getTransactionImpact(invoice);
                    const isPayment = invoice.isPayment === true;
                    const balanceAfter = runningBalance + impact;
                    const balanceBefore = runningBalance;
                    runningBalance = balanceAfter;

                    let debit = 0,
                      credit = 0;
                    if (impact > 0) debit = impact;
                    else if (impact < 0) credit = Math.abs(impact);

                    const products = invoice.products || [];

                    // Invoice with products → one row per product
                    if (!isPayment && products.length > 0) {
                      return products.map((product, pIdx) => {
                        const category = product.category?.toLowerCase() || "";
                        let size = "---",
                          detail = "";
                        if (category === "roll") {
                          size = product.size ? String(product.size) : "---";
                          detail = product.size ? `Size: ${product.size}` : "";
                        } else if (category === "reel" || category === "tota") {
                          size = product.length ? String(product.length) : "---";
                          detail = product.length ? `Length: ${product.length}` : "";
                        } else if (category === "packet") {
                          const parts = [];
                          if (product.size) parts.push(`Size: ${product.size}`);
                          if (product.ktae) parts.push(`KTAE: ${product.ktae}`);
                          if (product.gram) parts.push(`Gram: ${product.gram}`);
                          detail = parts.join(" | ");
                          size = product.size ? String(product.size) : "---";
                        } else {
                          size = product.size ? String(product.size) : "---";
                          detail = product.size ? `Size: ${product.size}` : "";
                        }

                        const productDesc = `${product.productName || ""}${
                          detail ? " / " + detail : ""
                        }`;
                        const productQty = product.quantity ? Number(product.quantity) : 0;
                        const productRate = product.rate ? Number(product.rate) : product.price ? Number(product.price) : 0;

                        let rowDebit = 0,
                          rowCredit = 0;
                        if (pIdx === 0) {
                          if (impact > 0) rowDebit = impact;
                          else if (impact < 0) rowCredit = Math.abs(impact);
                        }
                        const showBalance = pIdx === 0 ? balanceAfter : null;

                        return (
                          <tr
                            key={`${idx}-${pIdx}`}
                            style={invoice.status === "Return" ? { background: "#fff3cd" } : {}}
                          >
                            <td>{formatDateOnly(invoice.saleRecordTime || invoice.purchaseRecordTime)}</td>
                            <td>{pIdx === 0 ? `#${invoice.invoiceNumber}` : ""}</td>
                            <td style={{ textAlign: "center" }}>{size}</td>
                            <td>{productDesc}</td>
                            <td style={{ textAlign: "right" }}>{productQty}</td>
                            <td style={{ textAlign: "right" }}>{formatCurrencyComma(productRate)}</td>
                            <td style={{ textAlign: "right" }}>
                              {rowDebit > 0 ? formatCurrencyComma(rowDebit) : "---"}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {rowCredit > 0 ? formatCurrencyComma(rowCredit) : "---"}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: "bold" }}>
                              {showBalance !== null ? formatCurrencyComma(showBalance) : ""}
                            </td>
                          </tr>
                        );
                      });
                    }

                    // Payment or invoice without products
                    let description = "";
                    if (isPayment) {
                      description = "PAYMENT RECEIVED";
                      debit = 0;
                      credit = Number(invoice.amountPaid || 0);
                    } else {
                      description = `${invoice.status} - ${invoice.products?.length || 0} products`;
                    }

                    return (
                      <tr
                        key={idx}
                        style={
                          isPayment
                            ? { background: "#d4edda" }
                            : invoice.status === "Return"
                            ? { background: "#fff3cd" }
                            : {}
                        }
                      >
                        <td>{formatDateOnly(invoice.saleRecordTime || invoice.purchaseRecordTime || invoice.paymentTime)}</td>
                        <td>{isPayment ? "PAYMENT" : `#${invoice.invoiceNumber}`}</td>
                        <td style={{ textAlign: "center" }}>---</td>
                        <td>{description}</td>
                        <td style={{ textAlign: "right" }}>---</td>
                        <td style={{ textAlign: "right" }}>---</td>
                        <td style={{ textAlign: "right" }}>{debit > 0 ? formatCurrencyComma(debit) : "---"}</td>
                        <td style={{ textAlign: "right" }}>{credit > 0 ? formatCurrencyComma(credit) : "---"}</td>
                        <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatCurrencyComma(balanceAfter)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      ) : (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>No data to print</div>
      )}

      {hasMonthLedgers && ledgerData.totalBalance !== undefined && (
        <div
          style={{
            background: "#f8f9fa",
            padding: "10px",
            border: "1px solid #ddd",
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <strong>Total Outstanding Balance:</strong>
          <strong>{formatCurrencyComma(ledgerData.totalBalance)}</strong>
        </div>
      )}

      <div
        style={{
          textAlign: "center",
          marginTop: "20px",
          paddingTop: "10px",
          borderTop: "1px solid #ddd",
          fontSize: "9px",
          color: "#666",
        }}
      >
        --- End of Report ---
      </div>
    </div>
  );
});

// ----------------------
// CustomerLedger Page (updated with sorting and print fix)
// ----------------------
const CustomerLedger = () => {
  const [ledgerData, setLedgerData] = useState({ monthLedgers: [] });
  const [loading, setLoading] = useState(false);
  const [customerlist, setCustomerList] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());

  // ===== NEW: Balance Sheet state =====
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  const [balanceSheetMonth, setBalanceSheetMonth] = useState("");
  const [balanceSheetData, setBalanceSheetData] = useState([]);
  const [loadingBalanceSheet, setLoadingBalanceSheet] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const printRef = useRef();

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "--";
    const date = new Date(dateTime);
    return date.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return `${Number(amount).toFixed(2)}`;
  };

  // ===== COMPUTE INVOICES WITH MISSING RATES =====
  const invoicesWithMissingRates = useMemo(() => {
    if (!ledgerData.monthLedgers) return [];
    const result = [];
    ledgerData.monthLedgers.forEach(month => {
      (month.invoices || []).forEach(inv => {
        if (!inv.isPayment) {
          const hasMissingRate = (inv.products || []).some(p => {
            const rate = parseFloat(p.rate) || parseFloat(p.price) || 0;
            return rate === 0;
          });
          if (hasMissingRate) {
            result.push(inv.invoiceNumber);
          }
        }
      });
    });
    return result;
  }, [ledgerData]);

  const missingRateInvoiceNumbers = invoicesWithMissingRates.join(', ');
  const calculateInvoiceTotal = (invoice) => {
    const beforeGst = invoice.totalBeforeGst || 0;
    const discount = invoice.discount || 0;
    const rent = invoice.rent || 0;
    const gst = invoice.gstAmount || 0;
    return beforeGst + gst;
  };

  const filterZeroPayments = (invoices) => {
    return invoices.filter(invoice => {
      if (invoice.isPayment) {
        return invoice.amountPaid && Number(invoice.amountPaid) !== 0;
      }
      return true;
    });
  };

    // ✅ FIX: Use correct date field per invoice type (fixes Return invoices)
  const getTransactionDate = (invoice) => {
    const dateStr = getTransactionDateValue(invoice);
    return dateStr ? new Date(dateStr) : new Date(0);
  };

  // Sorting function: by invoice number ascending, payments at the end
  // ✅ FIX: Sort strictly by date, then by type (Sale → Return → Payment), then by invoice #
const sortTransactions = (transactions) => {
  return [...transactions].sort((a, b) => {
    // 1. Primary: sort by the correct date field
    const dateA = getTransactionDateValue(a);
    const dateB = getTransactionDateValue(b);

    const timeA = dateA ? new Date(dateA).getTime() : 0;
    const timeB = dateB ? new Date(dateB).getTime() : 0;

    if (timeA !== timeB) return timeA - timeB;

    // 2. Same date → order by type: Sale first, then Return, then Payment
    const getTypeOrder = (inv) => {
      if (inv.isPayment) return 3;
      if (inv.status === 'Return') return 2;
      return 1; // Sale / FactoryUse
    };

    const typeA = getTypeOrder(a);
    const typeB = getTypeOrder(b);
    if (typeA !== typeB) return typeA - typeB;

    // 3. Same type → sort by invoice number ascending
    const numA = parseInt(a.invoiceNumber) || 0;
    const numB = parseInt(b.invoiceNumber) || 0;
    return numA - numB;
  });
};

  // Compute net impact on balance for a single transaction
  const getTransactionImpact = (invoice) => {
    if (invoice.isPayment) {
      return -Number(invoice.amountPaid || 0);
    }
    const beforeGst = Number(invoice.totalBeforeGst || 0);
    const discount = Number(invoice.discount || 0);
    const rent = Number(invoice.rent || 0);
    const gst = Number(invoice.gstAmount || 0);
    const invoiceTotal = beforeGst + gst;
    const paid = Number(invoice.amountPaid || 0);
let impact = (invoice.status === 'Sale' || invoice.status === 'FactoryUse') ? invoiceTotal : -invoiceTotal;    impact -= paid;
    return impact;
  };

const handlePrint = () => {
  if (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) {
    alert("No data to print");
    return;
  }

  // ----- Helpers (local) -----
  const formatDateOnly = (dateTime) => {
    if (!dateTime) return "--";
    const date = new Date(dateTime);
    return date.toLocaleDateString();
  };

  const formatCurrencyComma = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "0.00";
    }
    return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Corrected impact for print: payment is separate, no subtraction of paid amount
  const getTransactionImpact = (invoice) => {
    if (invoice.isPayment) {
      return -Number(invoice.amountPaid || 0);
    }
    const beforeGst = Number(invoice.totalBeforeGst || 0);
    const gst = Number(invoice.gstAmount || 0);
    const invoiceTotal = beforeGst + gst;
    return (invoice.status === 'Sale' || invoice.status === 'FactoryUse') ? invoiceTotal : -invoiceTotal;
  };

 // ✅ FIX: Sort strictly by date, then by type (Sale → Return → Payment), then by invoice #
const sortTransactions = (transactions) => {
  return [...transactions].sort((a, b) => {
    // 1. Primary: sort by the correct date field
    const dateA = getTransactionDateValue(a);
    const dateB = getTransactionDateValue(b);

    const timeA = dateA ? new Date(dateA).getTime() : 0;
    const timeB = dateB ? new Date(dateB).getTime() : 0;

    if (timeA !== timeB) return timeA - timeB;

    // 2. Same date → order by type: Sale first, then Return, then Payment
    const getTypeOrder = (inv) => {
      if (inv.isPayment) return 3;
      if (inv.status === 'Return') return 2;
      return 1; // Sale / FactoryUse
    };

    const typeA = getTypeOrder(a);
    const typeB = getTypeOrder(b);
    if (typeA !== typeB) return typeA - typeB;

    // 3. Same type → sort by invoice number ascending
    const numA = parseInt(a.invoiceNumber) || 0;
    const numB = parseInt(b.invoiceNumber) || 0;
    return numA - numB;
  });
};

  const htmlParts = [];

  htmlParts.push(`
    <html>
    <head>
      <title>Customer Ledger - ${customerName || "All Customers"}</title>
      <style>
        @page { size: A4 landscape; margin: 15mm 10mm 15mm 10mm; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10px; padding: 0; margin: 0; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
        .header h1 { margin: 0 0 5px 0; font-size: 20px; color: #333; }
        .header h3 { margin: 0 0 3px 0; font-size: 16px; color: #666; }
        .header .period { font-size: 12px; color: #888; }
        .header .gen-date { font-size: 10px; color: #999; margin-top: 5px; }
        .month-header { background-color: #333; color: #fff; padding: 6px 10px; font-weight: bold; font-size: 11px; margin-top: 20px; margin-bottom: 8px; display: flex; justify-content: space-between; }
        .month-header span { margin-right: 15px; }
        table { width: 100%; border-collapse: collapse; font-size: 9px; margin-bottom: 20px; }
        table th { background-color: #e9ecef; border: 1px solid #000; padding: 4px 6px; text-align: left; font-weight: bold; }
        table td { border: 1px solid #000; padding: 3px 6px; vertical-align: middle; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .bold { font-weight: bold; }
        .opening-row { background-color: #f8f9fa; font-style: italic; }
        .payment-row { background-color: #d4edda; }
        .return-row { background-color: #fff3cd; }
        .debit { color: #28a745; }
        .credit { color: #dc3545; }
        .balance { font-weight: bold; }
        .page-break { page-break-before: always; }
        .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 9px; color: #666; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>CUSTOMER LEDGER</h1>
        <h3>${customerName || "All Customers"}</h3>
        <div class="period">Period: ${startMonth || "Start"} to ${endMonth || "End"}</div>
        <div class="gen-date">Generated on: ${new Date().toLocaleString()}</div>
      </div>
  `);

  // Process each month
  ledgerData.monthLedgers.forEach((monthData, monthIndex) => {
    const rawTransactions = monthData.invoices || [];
    const transactions = sortTransactions(rawTransactions);
    let runningBalance = Number(monthData.monthStartBalance || 0);

    htmlParts.push(`
      <div class="month-header">
        <span>Month: ${monthData.billingMonth}</span>
        <span>Start Balance: ${formatCurrencyComma(monthData.monthStartBalance)}</span>
        <span>End Balance: ${formatCurrencyComma(monthData.monthEndBalance)}</span>
        <span>Sales: ${formatCurrencyComma(monthData.monthTotalSales)}</span>
        <span>Returns: ${formatCurrencyComma(monthData.monthTotalReturns)}</span>
        <span>Payments: ${formatCurrencyComma(monthData.monthTotalPayments)}</span>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width:10%;">Date</th>
            <th style="width:8%;">Voucher #</th>
            <th style="width:8%;">Size</th>
            <th style="width:32%;">Description</th>
            <th style="width:6%; text-align:right;">Qty</th>
            <th style="width:8%; text-align:right;">Rate</th>
            <th style="width:10%; text-align:right;">Debit</th>
            <th style="width:10%; text-align:right;">Credit</th>
            <th style="width:12%; text-align:right;">Balance</th>
          </tr>
        </thead>
        <tbody>
    `);

    // Opening Balance row
    htmlParts.push(`
      <tr class="opening-row">
        <td>${monthData.billingMonth}-01</td>
        <td>---</td>
        <td>---</td>
        <td>Opening Balance</td>
        <td class="text-right">---</td>
        <td class="text-right">---</td>
        <td class="text-right">${formatCurrencyComma(monthData.monthStartBalance > 0 ? monthData.monthStartBalance : 0)}</td>
        <td class="text-right">${formatCurrencyComma(monthData.monthStartBalance < 0 ? Math.abs(monthData.monthStartBalance) : 0)}</td>
        <td class="text-right balance">${formatCurrencyComma(monthData.monthStartBalance)}</td>
      </tr>
    `);

    // Process each transaction
    transactions.forEach((invoice) => {
      const isPayment = invoice.isPayment === true;

      // ---- Payment ----
      if (isPayment) {
        const impact = getTransactionImpact(invoice);
        runningBalance += impact;
        let debit = 0, credit = 0;
        if (impact > 0) debit = impact;
        else if (impact < 0) credit = Math.abs(impact);
        htmlParts.push(`
          <tr class="payment-row">
            <td>${formatDateOnly(invoice.paymentTime)}</td>
            <td>PAYMENT</td>
            <td class="text-center">---</td>
            <td>PAYMENT RECEIVED</td>
            <td class="text-right">---</td>
            <td class="text-right">---</td>
            <td class="text-right ${debit > 0 ? 'debit' : ''}">${debit > 0 ? formatCurrencyComma(debit) : '---'}</td>
            <td class="text-right ${credit > 0 ? 'credit' : ''}">${credit > 0 ? formatCurrencyComma(credit) : '---'}</td>
            <td class="text-right balance">${formatCurrencyComma(runningBalance)}</td>
          </tr>
        `);
        return;
      }

      // ---- Invoice ----
      const products = invoice.products || [];
      const isSale = invoice.status === 'Sale' || invoice.status === 'FactoryUse';
      const invoiceTotal = (invoice.totalBeforeGst || 0) + (invoice.gstAmount || 0);
      const rowClass = invoice.status === 'Return' ? 'return-row' : '';

      // 1. Products
      products.forEach((product, pIdx) => {
        const productTotal = product.totalPrice || (product.quantity * product.rate);
        const debit = isSale ? productTotal : 0;
        const credit = isSale ? 0 : productTotal;
        const size = getSizeDisplay(product);
        const desc = `${product.productName || ''} / ${getDetail(product)}`;
        htmlParts.push(`
          <tr class="${rowClass}">
            <td>${formatDateOnly(getTransactionDateValue(invoice))}</td>
            <td>${pIdx === 0 ? `#${invoice.invoiceNumber}` : ''}</td>
            <td class="text-center">${size}</td>
            <td>${desc}</td>
            <td class="text-right">${product.quantity || 0}</td>
            <td class="text-right">${formatCurrencyComma(product.rate || product.price || 0)}</td>
            <td class="text-right ${debit > 0 ? 'debit' : ''}">${debit > 0 ? formatCurrencyComma(debit) : '---'}</td>
            <td class="text-right ${credit > 0 ? 'credit' : ''}">${credit > 0 ? formatCurrencyComma(credit) : '---'}</td>
            <td class="text-right"></td> <!-- balance empty -->
          </tr>
        `);
      });

      // 2. Discount
      if (isSale && invoice.discount > 0) {
        htmlParts.push(`
          <tr>
            <td>${formatDateOnly(getTransactionDateValue(invoice))}</td>
            <td></td>
            <td>---</td>
            <td>Discount</td>
            <td>---</td>
            <td>---</td>
            <td>---</td>
            <td class="text-right credit">${formatCurrencyComma(invoice.discount)}</td>
            <td></td>
          </tr>
        `);
      }

      // 3. Rent
      if (isSale && invoice.rent > 0) {
        htmlParts.push(`
          <tr>
            <td>${formatDateOnly(getTransactionDateValue(invoice))}</td>
            <td></td>
            <td>---</td>
            <td>Rent</td>
            <td>---</td>
            <td>---</td>
            <td class="text-right debit">${formatCurrencyComma(invoice.rent)}</td>
            <td>---</td>
            <td></td>
          </tr>
        `);
      }

      // 4. GST
      if (isSale && invoice.gstAmount > 0) {
        htmlParts.push(`
          <tr>
            <td>${formatDateOnly(getTransactionDateValue(invoice))}</td>
            <td></td>
            <td>---</td>
            <td>GST (${invoice.gstPercentage || 0}%)</td>
            <td>---</td>
            <td>---</td>
            <td class="text-right debit">${formatCurrencyComma(invoice.gstAmount)}</td>
            <td>---</td>
            <td></td>
          </tr>
        `);
      }

      // 5. Invoice Total – update balance
      const impact = isSale ? invoiceTotal : -invoiceTotal;
      runningBalance += impact;
      htmlParts.push(`
        <tr style="font-weight:bold; background:#e9ecef; border-bottom:2px solid #000;">
          <td>${formatDateOnly(getTransactionDateValue(invoice))}</td>
          <td></td>
          <td>---</td>
          <td>Invoice Total</td>
          <td>---</td>
          <td>---</td>
          <td class="text-right ${isSale ? 'debit' : ''}">${isSale ? formatCurrencyComma(invoiceTotal) : '---'}</td>
          <td class="text-right ${!isSale ? 'credit' : ''}">${!isSale ? formatCurrencyComma(invoiceTotal) : '---'}</td>
          <td class="text-right balance">${formatCurrencyComma(runningBalance)}</td>
        </tr>
      `);
    });

    htmlParts.push(`
        </tbody>
      </table>
    `);

    if (monthIndex < ledgerData.monthLedgers.length - 1) {
      htmlParts.push(`<div class="page-break"></div>`);
    }
  });

  // Total balance
  if (ledgerData.totalBalance !== undefined) {
    htmlParts.push(`
      <div style="background: #f8f9fa; padding: 10px; border: 1px solid #ddd; margin-top: 20px; font-size: 12px; display: flex; justify-content: space-between;">
        <span><strong>Total Outstanding Balance:</strong></span>
        <span><strong>${formatCurrencyComma(ledgerData.totalBalance)}</strong></span>
      </div>
    `);
  }

  htmlParts.push(`
      <div class="footer">--- End of Report ---</div>
    </body>
    </html>
  `);

  const finalHTML = htmlParts.join('');
  const printWindow = window.open('', '_blank', 'width=1024,height=800,scrollbars=yes');
  if (printWindow) {
    printWindow.document.write(finalHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  } else {
    alert('Please allow pop-ups for this site to print the ledger.');
  }
};

  useEffect(() => {
    api.get("/product/getAllCustomers")
      .then(res => { 
        if (res.data.code === 1) setCustomerList(res.data.additionalDetail); 
      })
      .catch(err => console.error(err));
  }, []);

  const fetchLedger = () => {
    if (!customerName) {
      alert("Please select a customer");
      return;
    }
    
    setLoading(true);
    setExpandedInvoices(new Set());
    
    const params = new URLSearchParams();
    if (startMonth) params.append('startMonth', startMonth);
    if (endMonth) params.append('endMonth', endMonth);
    
    const url = `/api/ledger/customer/${customerName}${params.toString() ? '?' + params.toString() : ''}`;
    
    api.get(url)
      .then(res => {
        setLedgerData(res.data);
      })
      .catch(err => {
        console.error(err);
        setLedgerData({ monthLedgers: [] });
      })
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setStartMonth("");
    setEndMonth("");
    setCustomerName("");
    setLedgerData({ monthLedgers: [] });
    setExpandedInvoices(new Set());
  };

  const toggleInvoiceDetails = (invoiceNumber) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceNumber)) {
      newExpanded.delete(invoiceNumber);
    } else {
      newExpanded.add(invoiceNumber);
    }
    setExpandedInvoices(newExpanded);
  };

  // ===== Balance Sheet handlers =====
    const fetchBalanceSheet = () => {
    setLoadingBalanceSheet(true);
    const params = new URLSearchParams();
    if (balanceSheetMonth) params.append('month', balanceSheetMonth);

    api.get(`/product/customer-balance-sheet?${params.toString()}`)
      .then(res => {
        // Sort customers alphabetically (A → Z) – case‑insensitive
        const sortedData = res.data.sort((a, b) =>
          a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' })
        );
        
        // 👇 FILTER: Exclude any customer with a zero balance
        const filteredData = sortedData.filter(item => Math.abs(Number(item.balance)) > 0.000001);

        setBalanceSheetData(filteredData);
      })
      .catch(err => {
        console.error(err);
        alert("Failed to fetch balance sheet");
      })
      .finally(() => setLoadingBalanceSheet(false));
  };

  const closeBalanceSheet = () => {
    setShowBalanceSheet(false);
    setBalanceSheetData([]);
  };

const printBalanceSheet = () => {
  if (!balanceSheetData || balanceSheetData.length === 0) {
    alert("No data to print");
    return;
  }

    const formatDateOnly = (dateTime) => {
    if (!dateTime) return "--";
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return "--";
    return date.toLocaleDateString();
  };
  // ---------------------------

  const formatCurrencyComma = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "0.00";
    }
    return Number(amount)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Compute total
  const total = balanceSheetData.reduce((sum, item) => sum + Number(item.balance || 0), 0);

  // Split data into two halves for two‑column layout
  const half = Math.ceil(balanceSheetData.length / 2);
  const leftData = balanceSheetData.slice(0, half);
  const rightData = balanceSheetData.slice(half);

  // Build HTML for the two columns
  const buildColumnRows = (data, startIndex) => {
  return data.map((item, idx) => {
    const index = startIndex + idx;

    const name = item.customerName || "—";
    const balance = Number(item.balance) || 0;

    const color = balance < 0 ? "#dc3545" : "#198754";

    const lastSaleDate = formatDateOnly(item.lastSaleDate);
    const lastPaymentDate = formatDateOnly(item.lastPaymentDate);

    return `
      <tr style="
        border-bottom: 1px solid #f1f3f5;
        background: ${index % 2 === 0 ? "#fff" : "#f8f9fa"};
      ">

        <td style="
          padding: 6px 8px;
          text-align: center;
          color: #6c757d;
          width: 30px;
        ">
          ${index + 1}
        </td>

        <td style="
          padding: 6px 8px;
          font-weight: 500;
          color: #212529;
        ">
          ${name}
        </td>

        <td style="
          padding: 6px 8px;
          color: #495057;
          white-space: nowrap;
        ">
          ${lastSaleDate}
        </td>

        <td style="
          padding: 6px 8px;
          color: #495057;
          white-space: nowrap;
        ">
          ${lastPaymentDate}
        </td>

        <td style="
          padding: 6px 8px;
          text-align: right;
          font-weight: 600;
          color: ${color};
          white-space: nowrap;
        ">
          ${formatCurrencyComma(balance)}
        </td>

      </tr>
    `;
  }).join("");
};

  const leftRows = buildColumnRows(leftData, 0);
  const rightRows = buildColumnRows(rightData, half);

  // Full HTML for the print window
  const printContent = `
    <div style="padding: 15px; font-family: 'Segoe UI', Roboto, Arial, sans-serif; max-width: 1100px; margin: 0 auto;">
      <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 12px;">
        <h1 style="margin: 0 0 5px 0; font-size: 24px; color: #212529;">Balance Sheet</h1>
        <h3 style="margin: 5px 0; font-size: 16px; color: #495057; font-weight: 400;">
          ${balanceSheetMonth ? `Month: ${balanceSheetMonth}` : "Latest Balances per Customer"}
        </h3>
        <div style="font-size: 12px; color: #6c757d; margin-top: 3px;">
          Generated on: ${new Date().toLocaleString()}
        </div>
      </div>

      <!-- Two‑column table -->
      <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
        <tr>
          <!-- Left column -->
          <td style="width: 50%; vertical-align: top; padding-right: 10px;">
            <table style="width: 100%; border-collapse: collapse; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
              <thead>
  <tr style="background: #f1f3f5; border-bottom: 2px solid #dee2e6;">

    <th style="
      padding: 8px 6px;
      text-align: center;
      font-weight: 600;
      color: #495057;
      width: 30px;
    ">
      #
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Customer
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Last Sale
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Last Payment
    </th>

    <th style="
      padding: 8px 6px;
      text-align: right;
      font-weight: 600;
      color: #495057;
    ">
      Balance
    </th>

  </tr>
</thead>
              <tbody>${leftRows}</tbody>
            </table>
          </td>
          <!-- Right column -->
          <td style="width: 50%; vertical-align: top; padding-left: 10px;">
            <table style="width: 100%; border-collapse: collapse; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
              <thead>
  <tr style="background: #f1f3f5; border-bottom: 2px solid #dee2e6;">

    <th style="
      padding: 8px 6px;
      text-align: center;
      font-weight: 600;
      color: #495057;
      width: 30px;
    ">
      #
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Customer
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Last Sale
    </th>

    <th style="
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      color: #495057;
    ">
      Last Payment
    </th>

    <th style="
      padding: 8px 6px;
      text-align: right;
      font-weight: 600;
      color: #495057;
    ">
      Balance
    </th>

  </tr>
</thead>
              <tbody>${rightRows}</tbody>
            </table>
          </td>
        </tr>
      </table>

      <!-- Total row (full width) -->
      <div style="margin-top: 15px; padding: 10px 15px; background: #e9ecef; border-radius: 4px; display: flex; justify-content: space-between; font-size: 14px; font-weight: 600;">
        <span>Total Outstanding</span>
        <span style="color: #0d6efd;">${formatCurrencyComma(total)}</span>
      </div>

      <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e9ecef; display: flex; justify-content: space-between; font-size: 11px; color: #6c757d;">
        <span>Total records: ${balanceSheetData.length}</span>
        <span>Generated on: ${new Date().toLocaleString()}</span>
      </div>
    </div>
  `;

  const win = window.open("", "_blank", "width=900,height=700");
  if (win) {
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Customer Balance Sheet</title>
        <style>
          @media print {
            @page { margin: 12mm 10mm; }
            body { margin: 0; padding: 0; }
          }
          body { font-family: 'Segoe UI', Roboto, Arial, sans-serif; padding: 15px; background: #fff; }
          table { page-break-inside: avoid; }
          td { vertical-align: top; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  }
};
  const exportLedgerToExcel = async () => {
  if (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) {
    alert("No ledger data to export.");
    return;
  }

  setIsExporting(true);
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Customer Ledger');

    worksheet.columns = [
      { width: 14 }, { width: 12 }, { width: 10 }, { width: 35 },
      { width: 10 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 16 }
    ];

    let currentRow = 1;

    // Helpers (local)
    const getTransactionImpact = (invoice) => {
      if (invoice.isPayment) return -Number(invoice.amountPaid || 0);
      const beforeGst = Number(invoice.totalBeforeGst || 0);
      const gst = Number(invoice.gstAmount || 0);
      const invoiceTotal = beforeGst + gst;
      return (invoice.status === 'Sale' || invoice.status === 'FactoryUse') ? invoiceTotal : -invoiceTotal;
    };

     // ✅ FIX: Use correct date field per invoice type (fixes Return invoices)
  const getTransactionDate = (invoice) => {
    const dateStr = getTransactionDateValue(invoice);
    return dateStr ? new Date(dateStr) : new Date(0);
  };

    // ✅ FIX: Sort strictly by date, then by type (Sale → Return → Payment), then by invoice #
const sortTransactions = (transactions) => {
  return [...transactions].sort((a, b) => {
    // 1. Primary: sort by the correct date field
    const dateA = getTransactionDateValue(a);
    const dateB = getTransactionDateValue(b);

    const timeA = dateA ? new Date(dateA).getTime() : 0;
    const timeB = dateB ? new Date(dateB).getTime() : 0;

    if (timeA !== timeB) return timeA - timeB;

    // 2. Same date → order by type: Sale first, then Return, then Payment
    const getTypeOrder = (inv) => {
      if (inv.isPayment) return 3;
      if (inv.status === 'Return') return 2;
      return 1; // Sale / FactoryUse
    };

    const typeA = getTypeOrder(a);
    const typeB = getTypeOrder(b);
    if (typeA !== typeB) return typeA - typeB;

    // 3. Same type → sort by invoice number ascending
    const numA = parseInt(a.invoiceNumber) || 0;
    const numB = parseInt(b.invoiceNumber) || 0;
    return numA - numB;
  });
};

    const formatDateOnly = (dateTime) => {
      if (!dateTime) return "--";
      return new Date(dateTime).toLocaleDateString();
    };

    // Header
    worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
    const titleRow = worksheet.getRow(currentRow);
    titleRow.height = 30;
    titleRow.getCell(1).value = 'CUSTOMER LEDGER';
    titleRow.eachCell((cell) => {
      cell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F75B5' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
    const custRow = worksheet.getRow(currentRow);
    custRow.height = 22;
    custRow.getCell(1).value = `Customer: ${customerName || "All Customers"}`;
    custRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: 'FF333333' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
    const periodRow = worksheet.getRow(currentRow);
    periodRow.height = 20;
    periodRow.getCell(1).value = `Period: ${startMonth || "Start"} to ${endMonth || "End"} | Generated: ${new Date().toLocaleString()}`;
    periodRow.eachCell((cell) => {
      cell.font = { size: 10, color: { argb: 'FF666666' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    currentRow++;
    currentRow++;

    // Loop months
    let grandTotal = 0;
    ledgerData.monthLedgers.forEach((monthData) => {
      const rawTransactions = monthData.invoices || [];
      const transactions = sortTransactions(rawTransactions);
      let runningBalance = Number(monthData.monthStartBalance || 0);

      // Month header
      worksheet.mergeCells(`A${currentRow}:I${currentRow}`);
      const monthHeader = worksheet.getRow(currentRow);
      monthHeader.height = 24;
      monthHeader.getCell(1).value = `Month: ${monthData.billingMonth}`;
      monthHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } };
        cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      });
      currentRow++;

      // Column headers
      const headers = ['Date', 'Voucher #', 'Size', 'Description', 'Qty', 'Rate', 'Debit', 'Credit', 'Balance'];
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 20;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      currentRow++;

      // Opening Balance
      const openRow = worksheet.addRow([
        `${monthData.billingMonth}-01`, '---', '---', 'Opening Balance', '---', '---',
        monthData.monthStartBalance > 0 ? monthData.monthStartBalance : 0,
        monthData.monthStartBalance < 0 ? Math.abs(monthData.monthStartBalance) : 0,
        monthData.monthStartBalance
      ]);
      openRow.eachCell((cell, col) => {
        if (col >= 7) cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
        cell.font = { italic: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
      });
      currentRow++;

      // Transactions
      transactions.forEach((invoice) => {
        const isPayment = invoice.isPayment === true;

        // Payment
        if (isPayment) {
          const impact = getTransactionImpact(invoice);
          runningBalance += impact;
          let debit = 0, credit = 0;
          if (impact > 0) debit = impact;
          else if (impact < 0) credit = Math.abs(impact);
          const row = worksheet.addRow([
            formatDateOnly(invoice.paymentTime),
            'PAYMENT',
            '---',
            'PAYMENT RECEIVED',
            '---',
            '---',
            debit,
            credit,
            runningBalance
          ]);
          row.eachCell((cell, col) => {
            if (col >= 7) cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
            if (col === 9) cell.font = { bold: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;
          return;
        }

        // Invoice
        const products = invoice.products || [];
        const isSale = invoice.status === 'Sale' || invoice.status === 'FactoryUse';
        const invoiceTotal = (invoice.totalBeforeGst || 0) + (invoice.gstAmount || 0);

        // 1. Products
        products.forEach((product, pIdx) => {
          const productTotal = product.totalPrice || (product.quantity * product.rate);
          const debit = isSale ? productTotal : 0;
          const credit = isSale ? 0 : productTotal;
          const size = getSizeDisplay(product);
          const desc = `${product.productName || ''} / ${getDetail(product)}`;
          const row = worksheet.addRow([
            formatDateOnly(invoice.saleRecordTime || invoice.purchaseRecordTime),
            pIdx === 0 ? `#${invoice.invoiceNumber}` : '',
            size,
            desc,
            product.quantity || 0,
            product.rate || product.price || 0,
            debit,
            credit,
            '' // balance empty
          ]);
          row.eachCell((cell, col) => {
            if (col >= 5 && col <= 9) { if (col === 5) cell.numFmt = '#,##0'; else cell.numFmt = '#,##0.00'; }
            cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;
        });

        // 2. Discount
        if (isSale && invoice.discount > 0) {
          const row = worksheet.addRow([
            formatDateOnly(invoice.saleRecordTime),
            '', '', 'Discount', '---', '---', 0, invoice.discount, ''
          ]);
          row.eachCell((cell, col) => {
            if (col >= 7) cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;
        }

        // 3. Rent
        if (isSale && invoice.rent > 0) {
          const row = worksheet.addRow([
            formatDateOnly(invoice.saleRecordTime),
            '', '', 'Rent', '---', '---', invoice.rent, 0, ''
          ]);
          row.eachCell((cell, col) => {
            if (col >= 7) cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;
        }

        // 4. GST
        if (isSale && invoice.gstAmount > 0) {
          const row = worksheet.addRow([
            formatDateOnly(invoice.saleRecordTime),
            '', '', `GST (${invoice.gstPercentage || 0}%)`, '---', '---', invoice.gstAmount, 0, ''
          ]);
          row.eachCell((cell, col) => {
            if (col >= 7) cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
          currentRow++;
        }

        // 5. Invoice Total – update balance
        // 5. Invoice Total – update balance
const impact = isSale ? invoiceTotal : -invoiceTotal;
runningBalance += impact;
const row = worksheet.addRow([
  formatDateOnly(invoice.saleRecordTime || invoice.purchaseRecordTime),
  '', '', 'Invoice Total', '---', '---',
  isSale ? invoiceTotal : 0,
  isSale ? 0 : invoiceTotal,
  runningBalance
]);
row.eachCell((cell, col) => {
  // Apply currency format for numeric columns (7,8,9)
  if (col >= 7) cell.numFmt = '#,##0.00';
  // Center align except description (col 4)
  cell.alignment = { horizontal: col === 4 ? 'left' : 'center', vertical: 'middle' };
  // Bold all cells in this row
  cell.font = { bold: true };
  // Add background fill (light blue)
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD4E6F1' }  // Light blue
  };
  // Border with a thicker top
  cell.border = {
    top: { style: 'medium' },   // thicker top
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };
});
currentRow++;
      });

      grandTotal += Number(monthData.monthEndBalance || 0);
      currentRow++;
    });

    // Grand Total
    if (ledgerData.totalBalance !== undefined) {
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const totalRow = worksheet.getRow(currentRow);
      totalRow.height = 25;
      totalRow.getCell(1).value = 'Total Outstanding Balance:';
      totalRow.getCell(9).value = ledgerData.totalBalance;
      totalRow.eachCell((cell, col) => {
        cell.font = { bold: true, size: 12 };
        cell.alignment = { horizontal: col === 9 ? 'center' : 'right', vertical: 'middle' };
        if (col === 9) {
          cell.font = { bold: true, size: 12, color: { argb: 'FF0D6EFD' } };
          cell.numFmt = '#,##0.00';
        }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
      });
      currentRow++;
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `ledger_${customerName || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Export error:", error);
    alert("Failed to export Excel. Check console.");
  } finally {
    setIsExporting(false);
  }
};

    const exportBalanceSheetToExcel = async () => {
    if (!balanceSheetData || balanceSheetData.length === 0) {
      alert("No balance sheet data to export.");
      return;
    }

    setIsExporting(true);
    try {
      // 1. Create Workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Balance Sheet');

      // 2. Set Column Widths
      worksheet.columns = [
  { width: 8 },   // #
  { width: 30 },  // Customer Name
  { width: 15 },  // Month
  { width: 18 },  // Last Sale Date
  { width: 18 },  // Last Payment Date
  { width: 18 },  // Balance
];

      let currentRow = 1;

      // --- [ TITLE ROW ] ---
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const titleRow = worksheet.getRow(currentRow);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'BALANCE SHEET';
      titleRow.eachCell((cell) => {
        cell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } }; // Green
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow++;

      // --- [ SUBTITLE ROW ] ---
      worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
      const subRow = worksheet.getRow(currentRow);
      subRow.height = 20;
      subRow.getCell(1).value = `Month: ${balanceSheetMonth || "Latest Balances"} | Generated: ${new Date().toLocaleString()}`;
      subRow.eachCell((cell) => {
        cell.font = { size: 10, color: { argb: 'FF666666' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      currentRow++;
      currentRow++; // blank gap

      // --- [ COLUMN HEADERS ] ---
      const headers = ['#', 'Customer Name', 'Month','Last Sale Date',
  'Last Payment Date', 'Balance'];
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 20;
      headerRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 11, color: { argb: 'FF000000' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } }; // Light Grey
        cell.alignment = { horizontal: colNumber === 2 ? 'left' : 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      });
      currentRow++;

      // --- [ DATA ROWS ] ---
      let grandTotal = 0;
      
      balanceSheetData.forEach((item, index) => {
        const balance = Number(item.balance) || 0;
        grandTotal += balance;

        const row = worksheet.addRow([
  index + 1,
  item.customerName || '—',
  item.billingMonth || 'N/A',
  item.lastSaleDate ? formatDateOnly(item.lastSaleDate) : '--',
item.lastPaymentDate ? formatDateOnly(item.lastPaymentDate) : '--',
  balance
]);

        row.eachCell((cell, colNumber) => {

  cell.border = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };

  if (colNumber === 1) {

    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };

  } else if (colNumber === 2) {

    cell.alignment = {
      horizontal: 'left',
      vertical: 'middle'
    };

    cell.font = {
      bold: true
    };

  } else if (colNumber === 3) {

    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };

  } else if (colNumber === 4 || colNumber === 5) {

    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };

  } else if (colNumber === 6) {

    cell.alignment = {
      horizontal: 'right',
      vertical: 'middle'
    };

    cell.numFmt = '#,##0.00';

    if (balance < 0) {
      cell.font = {
        color: {
          argb: 'FFDC3545'
        }
      };
    }
  }
});
        currentRow++;
      });

      // --- [ GRAND TOTAL ROW ] ---
      worksheet.mergeCells(`A${currentRow}:E${currentRow}`);
      const totalRow = worksheet.getRow(currentRow);
      totalRow.height = 22;
      totalRow.getCell(1).value = 'Total Outstanding Balance';
      totalRow.getCell(6).value = grandTotal;
      
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 12 };
        cell.alignment = { horizontal: colNumber === 4 ? 'right' : 'right', vertical: 'middle' };
        if (colNumber === 4) {
          cell.font = { bold: true, size: 12, color: { argb: 'FF0D6EFD' } };
          cell.numFmt = '#,##0.00';
        }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
      });
      currentRow++;

      // --- [ DOWNLOAD ] ---
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `balance_sheet_${balanceSheetMonth || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Balance Sheet.");
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- Render ----------
  return (
    <>
      <div className="purchase-container">
        <h2 className="page-title">Customer Ledger</h2>

        {/* FILTERS SECTION */}
        <div className="filter-section" style={{ 
          background: "#f8f9fa", 
          padding: "15px", 
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #dee2e6"
        }}>
          {/* ===== MISSING RATE WARNING ===== */}
{invoicesWithMissingRates.length > 0 && (
  <div style={{
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '10px 15px',
    borderRadius: '4px',
    marginBottom: '15px',
    border: '1px solid #ffeeba',
    fontWeight: '500'
  }}>
    ⚠️ Warning: The following invoice(s) have missing product rates: <strong>{missingRateInvoiceNumbers}</strong>. Please update them.
  </div>
)}
          <div className="filter-row" style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "15px" }}>
            <div className="filter-group">
              <label className="filter-label">Customer Name *</label>
              <div style={{ display: "flex", gap: "10px" }}>
                <input
                  type="text"
                  list="customerNames"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Select customer"
                  className="form-control"
                  style={{ width: "250px" }}
                />
                <datalist id="customerNames">
                  {customerlist.map((c, i) => (
                    <option key={i} value={c.customerName} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Start Month</label>
              <input
                type="month"
                value={startMonth}
                onChange={e => setStartMonth(e.target.value)}
                className="form-control"
                style={{ width: "180px" }}
              />
            </div>
            
            <div className="filter-group">
              <label className="filter-label">End Month</label>
              <input
                type="month"
                value={endMonth}
                onChange={e => setEndMonth(e.target.value)}
                className="form-control"
                style={{ width: "180px" }}
              />
            </div>
          </div>

          <div className="filter-buttons" style={{ display: "flex", gap: "10px" }}>
            <button 
              className="btn btn-primary"
              onClick={fetchLedger}
              disabled={!customerName}
            >
              <i className="bi bi-search" style={{ marginRight: "5px" }}></i>
              Fetch Ledger
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={clearFilters}
            >
              <i className="bi bi-x-circle" style={{ marginRight: "5px" }}></i>
              Clear All
            </button>

            <button 
              className="btn btn-info"
              onClick={() => setShowBalanceSheet(true)}
            >
              <i className="bi bi-file-spreadsheet" style={{ marginRight: "5px" }}></i>
              Balance Sheet
            </button>
            
            <button 
              className="btn btn-success"
              onClick={handlePrint} 
              disabled={!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0}
              style={{ marginLeft: "auto" }}
            >
              <i className="bi bi-printer" style={{ marginRight: "5px" }}></i>
              Print Ledger
            </button>
            <button 
              className="btn btn-primary"
              onClick={exportLedgerToExcel}
              disabled={isExporting || !ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0}
              style={{ marginLeft: "10px", backgroundColor: "#217346", borderColor: "#217346" }}
            >
              <i className="bi bi-file-earmark-excel" style={{ marginRight: "5px" }}></i>
              {isExporting ? "Exporting..." : "Export to Excel"}
            </button>
          </div>
          
          <div className="filter-info" style={{ marginTop: "10px", fontSize: "12px", color: "#6c757d" }}>
            <i className="bi bi-info-circle" style={{ marginRight: "5px" }}></i>
            Leave month fields empty to get all records. Format: YYYY-MM (e.g., 2024-01)
          </div>
        </div>

        {/* ===== BALANCE SHEET MODAL ===== */}
        {showBalanceSheet && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "20px",
            }}
            onClick={closeBalanceSheet}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "30px 35px",
                maxWidth: "900px",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "2px solid #e9ecef",
                  paddingBottom: "15px",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "600", color: "#212529" }}>
                    <i className="bi bi-file-spreadsheet" style={{ marginRight: "12px", color: "#0d6efd" }}></i>
                    Balance Sheet
                  </h2>
                  <p style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#6c757d" }}>
                    {balanceSheetMonth ? `Month: ${balanceSheetMonth}` : "Latest balances per customer"}
                  </p>
                </div>
                <button
                  className="btn-close"
                  onClick={closeBalanceSheet}
                  style={{ fontSize: "20px" }}
                ></button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  marginBottom: "25px",
                  background: "#f8f9fa",
                  padding: "15px 20px",
                  borderRadius: "8px",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "#495057", marginBottom: "4px" }}>
                    Select Month (optional)
                  </label>
                  <input
                    type="month"
                    value={balanceSheetMonth}
                    onChange={(e) => setBalanceSheetMonth(e.target.value)}
                    className="form-control"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #ced4da" }}
                  />
                  <small style={{ color: "#6c757d" }}>Leave empty for latest balance per customer</small>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button
                    className="btn btn-primary"
                    onClick={fetchBalanceSheet}
                    disabled={loadingBalanceSheet}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      background: "#0d6efd",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {loadingBalanceSheet ? (
                      <><span className="spinner-border spinner-border-sm" role="status"></span> Loading...</>
                    ) : (
                      <><i className="bi bi-search"></i> Fetch</>
                    )}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={exportBalanceSheetToExcel}
                    disabled={isExporting || !balanceSheetData || balanceSheetData.length === 0}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      background: "#217346",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="bi bi-file-earmark-excel"></i>
                    {isExporting ? " Exporting..." : " Export to Excel"}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={printBalanceSheet}
                    disabled={!balanceSheetData || balanceSheetData.length === 0}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      background: "#198754",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i className="bi bi-printer"></i> Print
                  </button>
                </div>
              </div>

              {balanceSheetData.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    }}
                  >
                    <thead>
  <tr style={{ background: "#f1f3f5", borderBottom: "2px solid #dee2e6" }}>
    <th
      style={{
        padding: "12px 15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057",
        width: "50px"
      }}
    >
      #
    </th>

    <th
      style={{
        padding: "12px 15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057"
      }}
    >
      Customer Name
    </th>

    <th
      style={{
        padding: "12px 15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057"
      }}
    >
      Month
    </th>

    <th
      style={{
        padding: "12px 15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057"
      }}
    >
      Last Sale Date
    </th>

    <th
      style={{
        padding: "12px 15px",
        textAlign: "left",
        fontWeight: "600",
        color: "#495057"
      }}
    >
      Last Payment Date
    </th>

    <th
      style={{
        padding: "12px 15px",
        textAlign: "right",
        fontWeight: "600",
        color: "#495057"
      }}
    >
      Balance
    </th>
  </tr>
</thead>
                    <tbody>
  {balanceSheetData.map((item, index) => (
    <tr
      key={index}
      style={{
        borderBottom: "1px solid #f1f3f5",
        background: index % 2 === 0 ? "#fff" : "#f8f9fa",
      }}
    >
      {/* # */}
      <td
        style={{
          padding: "12px 15px",
          textAlign: "center",
          color: "#6c757d"
        }}
      >
        {index + 1}
      </td>

      {/* Customer Name */}
      <td
        style={{
          padding: "12px 15px",
          fontWeight: "500",
          color: "#212529"
        }}
      >
        {item.customerName}
      </td>

      {/* Month */}
      <td
        style={{
          padding: "12px 15px",
          color: "#495057"
        }}
      >
        {item.billingMonth || "--"}
      </td>

      {/* Last Sale Date */}
      <td
        style={{
          padding: "12px 15px",
          color: "#495057"
        }}
      >
        {item.lastSaleDate
          ? new Date(item.lastSaleDate).toLocaleDateString()
          : "--"}
      </td>

      {/* Last Payment Date */}
      <td
        style={{
          padding: "12px 15px",
          color: "#495057"
        }}
      >
        {item.lastPaymentDate
          ? new Date(item.lastPaymentDate).toLocaleDateString()
          : "--"}
      </td>

      {/* Balance */}
      <td
        style={{
          padding: "12px 15px",
          textAlign: "right",
          fontWeight: "600",
          color: Number(item.balance) < 0 ? "#dc3545" : "#198754",
        }}
      >
        {formatCurrency(item.balance)}
      </td>
    </tr>
  ))}
</tbody>
                    <tfoot>
                      <tr style={{ background: "#e9ecef", borderTop: "2px solid #dee2e6" }}>
                        <td
  colSpan="5"
  style={{
    padding: "14px 15px",
    textAlign: "right",
    fontWeight: "600",
    fontSize: "15px"
  }}
>
  Total Outstanding
</td>
                        <td
                          style={{
                            padding: "14px 15px",
                            textAlign: "right",
                            fontWeight: "700",
                            fontSize: "16px",
                            color: "#0d6efd",
                            background: "#e9ecef",
                          }}
                        >
                          {formatCurrency(balanceSheetData.reduce((sum, item) => sum + Number(item.balance || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#6c757d",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  {loadingBalanceSheet ? (
                    <><span className="spinner-border text-primary" role="status"></span><p style={{ marginTop: "15px" }}>Loading balance sheet...</p></>
                  ) : (
                    <>
                      <i className="bi bi-file-spreadsheet" style={{ fontSize: "48px", opacity: "0.5" }}></i>
                      <p style={{ marginTop: "15px" }}>No data found. Select a month or click "Fetch".</p>
                    </>
                  )}
                </div>
              )}

              <div
                style={{
                  marginTop: "20px",
                  paddingTop: "15px",
                  borderTop: "1px solid #e9ecef",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  color: "#6c757d",
                }}
              >
                <span>Total records: {balanceSheetData.length}</span>
                <span>Last updated: {new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="loading-state" style={{ textAlign: "center", padding: "40px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: "10px" }}>Loading ledger data...</p>
          </div>
        )}

        {!loading && ledgerData.monthLedgers && ledgerData.monthLedgers.length > 0 && (
          <div className="summary-section" style={{ 
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "20px", 
            borderRadius: "10px",
            marginBottom: "25px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
          }}>
            <h3 style={{ marginTop: "0", marginBottom: "15px", display: "flex", alignItems: "center" }}>
              <i className="bi bi-bar-chart" style={{ marginRight: "10px" }}></i>
              Summary
            </h3>
            <div className="summary-grid" style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
              gap: "20px" 
            }}>
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Outstanding Balance</div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>{formatCurrency(ledgerData.totalBalance)}</div>
              </div>
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Period</div>
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>{startMonth || "Start"} to {endMonth || "End"}</div>
              </div>
              <div className="summary-item">
                <div style={{ fontSize: "12px", opacity: "0.9" }}>Total Months</div>
                <div style={{ fontSize: "24px", fontWeight: "bold" }}>{ledgerData.monthLedgers.length}</div>
              </div>
            </div>
          </div>
        )}

        {!loading && ledgerData.monthLedgers && ledgerData.monthLedgers.length > 0 && ledgerData.monthLedgers.map((monthData, monthIdx) => {
          const rawTransactions = filterZeroPayments(monthData.invoices || []);
          // Sort transactions by invoice number (ascending)
          const transactions = sortTransactions(rawTransactions);
          
          let runningBalance = Number(monthData.monthStartBalance || 0);

          return (
            <div key={monthIdx} className="month-section" style={{ 
              marginBottom: "30px",
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}>
              {/* Month header */}
              <div style={{ 
                background: "#f8f9fa", 
                padding: "15px 20px",
                borderBottom: "2px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <div style={{ 
                    background: "#007bff", 
                    color: "white", 
                    padding: "5px 12px", 
                    borderRadius: "20px",
                    fontWeight: "bold",
                    fontSize: "14px"
                  }}>
                    {monthData.billingMonth}
                  </div>
                  <div style={{ fontSize: "14px", color: "#6c757d" }}>
                    {transactions.length} transactions
                  </div>
                </div>
                <div style={{ display: "flex", gap: "15px", fontSize: "14px", flexWrap: "wrap" }}>
                  <div><span style={{ color: "#6c757d" }}>Start:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthStartBalance)}</span></div>
                  <div><span style={{ color: "#6c757d" }}>End:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthEndBalance)}</span></div>
                  <div><span style={{ color: "#28a745" }}>Sales:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalSales)}</span></div>
                  <div><span style={{ color: "#dc3545" }}>Returns:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalReturns)}</span></div>
                  <div><span style={{ color: "#17a2b8" }}>Payments:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalPayments)}</span></div>
                  <div><span style={{ color: "#ffc107" }}>Discounts:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalDiscounts)}</span></div>
                  <div><span style={{ color: "#fd7e14" }}>Rents:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalRents)}</span></div>
                  <div><span style={{ color: "#6f42c1" }}>GST:</span> <span style={{ fontWeight: "bold" }}>{formatCurrency(monthData.monthTotalGst)}</span></div>
                </div>
              </div>

              <div className="invoices-list" style={{ padding: "0" }}>
                {transactions.length > 0 ? (
                  transactions.map((invoice, idx) => {
                    const impact = getTransactionImpact(invoice);
                    const balanceBefore = runningBalance;
                    const balanceAfter = balanceBefore + impact;
                    runningBalance = balanceAfter;

                    const isExpanded = expandedInvoices.has(invoice.invoiceNumber);
                    const isPayment = invoice.isPayment === true;
                    const invoiceTotal = calculateInvoiceTotal(invoice);

                    const hasMissingRateForInvoice = !isPayment && (invoice.products || []).some(p => {
  const rate = parseFloat(p.rate) || parseFloat(p.price) || 0;
  return rate === 0;
});

                    return (
                      <div key={idx} className="invoice-card" style={{ 
                        borderBottom: "1px solid #f0f0f0",
                        padding: "15px 20px",
                        background: isPayment ? "#f0fff4" : (idx % 2 === 0 ? "#fff" : "#fafafa")
                      }}>
                        {hasMissingRateForInvoice && (
                          <div style={{
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            marginBottom: '10px',
                            border: '1px solid #ffeeba',
                            fontWeight: '500',
                            fontSize: '13px'
                          }}>
                            ⚠️ Warning: This invoice contains product(s) with missing rate (price). Please update.
                          </div>
                        )}
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          marginBottom: isExpanded && !isPayment ? "15px" : "0",
                          flexWrap: "wrap",
                          gap: "10px"
                        }}>
                          {/* Left side */}
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              {isPayment ? (
                                <>
                                  <span style={{ fontSize: "16px", fontWeight: "bold", color: "#28a745" }}>Payment Received</span>
                                  <span style={{ background: "#d4edda", color: "#155724", padding: "2px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold" }}>PAYMENT</span>
                                </>
                              ) : (
                                <>
                                  <span style={{ fontSize: "16px", fontWeight: "bold", color: invoice.status === "Sale" ? "#28a745" : "#dc3545" }}>Invoice #{invoice.invoiceNumber}</span>
                                  <span style={{ background: invoice.status === "Sale" ? "#d4edda" : "#f8d7da", color: invoice.status === "Sale" ? "#155724" : "#721c24", padding: "2px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold" }}>{invoice.status}</span>
                                </>
                              )}
                            </div>
                            {!isPayment && (
                              <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>
                                <span style={{ marginRight: "15px" }}><i className="bi bi-calendar" style={{ marginRight: "3px" }}></i>Sale: {formatDateTime(invoice.saleRecordTime)}</span>
                                <span><i className="bi bi-arrow-return-left" style={{ marginRight: "3px" }}></i>Return: {formatDateTime(invoice.returnRecordTime)}</span>
                              </div>
                            )}
                            {isPayment && (
                              <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>
                                <i className="bi bi-clock" style={{ marginRight: "3px" }}></i>Payment Time: {formatDateTime(invoice.paymentTime)}
                              </div>
                            )}
                          </div>

                          {/* Right side */}
                          <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                            {!isPayment && (
                              <div style={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "flex-end", backgroundColor: "#f8f9fa", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e0e0e0", minWidth: "250px" }}>
                                <div style={{ fontSize: "11px", color: "#495057", fontWeight: "bold" }}>Invoice Breakdown</div>
                                <div style={{ fontSize: "11px", color: "#6c757d", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                                  <span>Subtotal:</span><strong style={{ color: "#20c997", textAlign: "right" }}>{formatCurrency(invoice.totalBeforeGst)}</strong>
                                  <span>Discount:</span><strong style={{ color: "#ffc107", textAlign: "right" }}>- {formatCurrency(invoice.discount || 0)}</strong>
                                  <span>Rent:</span><strong style={{ color: "#fd7e14", textAlign: "right" }}>+ {formatCurrency(invoice.rent || 0)}</strong>
                                  <span>Before GST:</span><strong style={{ color: "#6f42c1", textAlign: "right" }}>{formatCurrency(invoice.totalBeforeGst || 0)}</strong>
                                  <span>GST ({invoice.gstPercentage || 0}%):</span><strong style={{ color: "#dc3545", textAlign: "right" }}>{formatCurrency(invoice.gstAmount)}</strong>
                                </div>
                                <div style={{ fontSize: "13px", fontWeight: "bold", borderTop: "1px solid #dee2e6", paddingTop: "4px", width: "100%", textAlign: "right" }}>Total: {formatCurrency(invoiceTotal)}</div>
                              </div>
                            )}
                            {!isPayment && invoice.amountPaid > 0 && (
                              <div style={{ textAlign: "right", backgroundColor: "#e8f4ff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #99ccff", minWidth: "120px" }}>
                                <div style={{ fontSize: "11px", color: "#0066cc" }}><i className="bi bi-cash-stack" style={{ marginRight: "3px" }}></i>Paid</div>
                                <div style={{ fontSize: "16px", fontWeight: "bold", color: "#0066cc" }}>{formatCurrency(invoice.amountPaid)}</div>
                              </div>
                            )}
                            {isPayment && (
                              <div style={{ textAlign: "right", backgroundColor: "#d4edda", padding: "8px 12px", borderRadius: "6px", border: "1px solid #a3cfbb", minWidth: "120px" }}>
                                <div style={{ fontSize: "11px", color: "#155724" }}><i className="bi bi-cash" style={{ marginRight: "3px" }}></i>Payment</div>
                                <div style={{ fontSize: "16px", fontWeight: "bold", color: "#155724" }}>{formatCurrency(invoice.amountPaid)}</div>
                              </div>
                            )}
                            <div style={{ textAlign: "right", backgroundColor: "#fff3cd", padding: "8px 12px", borderRadius: "6px", border: "1px solid #ffeeba", minWidth: "140px" }}>
                              <div style={{ fontSize: "11px", color: "#856404" }}><i className="bi bi-wallet2" style={{ marginRight: "3px" }}></i>Remaining Balance</div>
                              <div style={{ fontSize: "16px", fontWeight: "bold", color: "#856404" }}>{formatCurrency(balanceAfter)}</div>
                              {!isPayment && invoice.amountPaid > 0 && (
                                <div style={{ fontSize: "9px", color: "#856404", opacity: "0.8" }}>Total: {formatCurrency(invoiceTotal)} - Paid: {formatCurrency(invoice.amountPaid)}</div>
                              )}
                            </div>
                            {!isPayment && (
                              <button onClick={() => toggleInvoiceDetails(invoice.invoiceNumber)} style={{ background: "none", border: "1px solid #007bff", color: "#007bff", padding: "5px 15px", borderRadius: "5px", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                                <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                                {isExpanded ? "Hide" : "Show"} Products ({invoice.products?.length || 0})
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expandable product details */}
                        {!isPayment && isExpanded && invoice.products && invoice.products.length > 0 && (
                          <div className="product-details" style={{ marginTop: "15px", border: "1px solid #dee2e6", borderRadius: "8px", overflow: "hidden" }}>
                            <div style={{ background: "#f8f9fa", padding: "10px 15px", borderBottom: "1px solid #dee2e6", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                              <div><i className="bi bi-box-seam" style={{ marginRight: "8px" }}></i>Products ({invoice.products.length})</div>
                              <div style={{ fontSize: "12px", color: "#6c757d", display: "flex", gap: "15px", flexWrap: "wrap" }}>
                                <span>Subtotal: <strong style={{ color: "#20c997" }}>{formatCurrency(invoice.totalBeforeGst)}</strong></span>
                                <span>Discount: <strong style={{ color: "#ffc107" }}>-{formatCurrency(invoice.discount || 0)}</strong></span>
                                <span>Rent: <strong style={{ color: "#fd7e14" }}>+{formatCurrency(invoice.rent || 0)}</strong></span>
                                <span>GST ({invoice.gstPercentage || 0}%): <strong style={{ color: "#dc3545" }}>{formatCurrency(invoice.gstAmount)}</strong></span>
                                <span>Total: <strong>{formatCurrency(invoiceTotal)}</strong></span>
                                {invoice.amountPaid > 0 && <span style={{ color: "#0066cc" }}>Paid: <strong>{formatCurrency(invoice.amountPaid)}</strong></span>}
                              </div>
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr style={{ background: "#e9ecef" }}>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>#</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Product Name</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Category</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Size</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Quantity</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Rate</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>KTAE</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Gram</th>
                                  <th style={{ padding: "10px 15px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Total Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.products.map((product, pidx) => {
                                  const category = product.category?.toLowerCase() || '';
                                  const showSizeField = category === 'roll' || category === 'reel' || category === 'packet' || category === 'tota';
                                  const showKtae = category === 'packet';
                                  const showGram = category === 'packet';
                                  const getSizeOrLength = () => {
                                    if (!showSizeField) return '--';
                                    if (category === 'reel' || category === 'tota') return product.length || '--';
                                    return product.size || '--';
                                  };
                                  return (
                                    <tr key={pidx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{pidx + 1}</td>
                                      <td style={{ padding: "10px 15px", fontWeight: "500" }}>{product.productName}</td>
                                      <td style={{ padding: "10px 15px", color: "#6c757d" }}>{product.category}</td>
                                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{getSizeOrLength()}</td>
                                      <td style={{ padding: "10px 15px", textAlign: "center", fontWeight: "bold" }}>{product.quantity}</td>
                                      <td style={{ padding: "10px 15px", textAlign: "right" }}>{product.rate ? formatCurrency(product.rate) : '--'}</td>
                                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{showKtae ? (product.ktae || '--') : '--'}</td>
                                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{showGram ? (product.gram || '--') : '--'}</td>
                                      <td style={{ padding: "10px 15px", fontWeight: "bold", color: "#28a745", textAlign: "right" }}>{formatCurrency(product.totalPrice)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "30px", textAlign: "center", color: "#6c757d" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "10px", opacity: "0.5" }}></i>
                    <p>No transactions found for this month</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {!loading && (!ledgerData.monthLedgers || ledgerData.monthLedgers.length === 0) && (
          <div style={{ textAlign: "center", padding: "50px 20px", background: "#f8f9fa", borderRadius: "10px", border: "2px dashed #dee2e6", marginTop: "20px" }}>
            <i className="bi bi-journal-text" style={{ fontSize: "48px", color: "#adb5bd", marginBottom: "15px" }}></i>
            <h4 style={{ color: "#6c757d" }}>No Ledger Data Found</h4>
            <p style={{ color: "#6c757d", maxWidth: "500px", margin: "0 auto" }}>
              {customerName ? `No transactions found for customer "${customerName}"${startMonth || endMonth ? ` between ${startMonth || "start"} and ${endMonth || "end"}` : ""}` : "Select a customer and click 'Fetch Ledger' to view their transaction history"}
            </p>
          </div>
        )}
      </div>

      {/* Hidden print component (still used if you ever call it) */}
      <div style={{ display: "none" }}>
        <LedgerPrint
          ref={printRef}
          ledgerData={ledgerData}
          customerName={customerName}
          startMonth={startMonth}
          endMonth={endMonth}
        />
      </div>
    </>
  );
};

export default CustomerLedger;